import Vue, { Component, CreateElement } from "vue";

import ResourceItemReference from "@/components/resource/ResourceItemReference";
import { InjectionKey } from "@vue/composition-api";
import OptionLabel from "./OptionLabel";
import DefinitionField from "@/components/DefinitionField.vue";

export const sheetOptionsSymbol: InjectionKey<SheetHighlights> = Symbol();

export type SheetHighlights = { [key: string]: boolean | SheetHighlights };

interface DefinitionFieldProps {
  label: string;
  name: string;
}

const definitionFieldHoc = (component: Component | string) =>
  Vue.extend<DefinitionFieldProps>({
    name: "SheetField",
    functional: true,
    inject: { highlights: { from: sheetOptionsSymbol as symbol, default: {} } },

    render(h: CreateElement, { props, data, listeners, slots, injections }) {
      const highlight = injections.highlights
        ? injections.highlights[props.name] || false
        : false;

      return h(DefinitionField, { props: { ...props, highlight } }, [
        h(component, { attrs: data.attrs, on: listeners }, slots().default),
      ]);
    },
  });

const stringField = Vue.extend<{ value: string | object }>({
  functional: true,
  render(h: CreateElement, { props, data: { attrs }, listeners, slots }) {
    let value = props.value;
    if (value != null && typeof value === "object") value = value.toString();
    return h("div", { attrs, on: listeners }, [
      value != null ? value : slots().default != null ? slots().default : "-",
    ]);
  },
});

export const StringEntry = definitionFieldHoc(stringField);

export const ItemRefEntry = definitionFieldHoc(ResourceItemReference);

const booleandField = Vue.extend<{ value: boolean }>({
  functional: true,
  render(h: CreateElement, { props, data: { attrs }, listeners }) {
    return h("span", { attrs, on: listeners }, [props.value ? "Tak" : "Nie"]);
  },
});

export const BooleanEntry = definitionFieldHoc(booleandField);

const TextField = Vue.extend<{ value: string }>({
  functional: true,
  render(h: CreateElement, { props, data: { attrs }, listeners, slots }) {
    return h("div", { attrs, on: listeners, style: "white-space:pre-wrap" }, [
      props.value ? props.value : slots().default ? slots().default : "-",
    ]);
  },
});

export const TextEntry = definitionFieldHoc(TextField);

const UrlField = Vue.extend<{ value: string }>({
  functional: true,
  render(h: CreateElement, { props, data: { attrs }, listeners, slots }) {
    const link = props.value ? props.value : slots().default;
    return h(
      "a",
      { attrs: { ...attrs, href: link, target: "_blank" }, on: listeners },
      [link ? link : "-"],
    );
  },
});

export const UrlEntry = definitionFieldHoc(UrlField);

export const OptionLabelEntry = definitionFieldHoc(OptionLabel);

const DateTimeField = Vue.extend<{ value: Date }>({
  functional: true,
  render(h: CreateElement, { props, data: { attrs }, listeners, slots }) {
    return h("div", { attrs, on: listeners }, [
      props.value
        ? props.value.toLocaleString()
        : slots().default
        ? slots().default.toLocaleString()
        : "-",
    ]);
  },
});

export const DateTimeEntry = definitionFieldHoc(DateTimeField);
